<template>
    <div>
        <v-hover v-if="showExport&&!mobileMode">
            <div slot-scope="{ hover }" class="caption grey--text">
                <v-layout style="min-height:40px;">
                    <v-spacer></v-spacer>
                    <span v-show="hover" ma-0 pa-0>
                        <v-btn @click="downloadCSV" outline small flat round class="grey--text" ma-0 pa-0>
                            {{'Download CSV'|translate}}
                        </v-btn>
                    </span>
                </v-layout>
            </div>
        </v-hover>
        <v-chart v-if="option" ref="chart" :style="{height:height+'px'}" :option="option" :theme="theme" :update-options="updateOptions" @click="handleClick" />
    </div>
</template>

<script>
import * as echarts from 'echarts';
import 'echarts-gl';
import VChart from "vue-echarts";
import {exportCSVFromDicts} from "@/tocsv.js"

export default {
    name: "VueEchart",
    components: {
        VChart
    },
    props: {
        whiteMode: {type: Boolean, default: false},
        transparentMode: {type: Boolean, default: false},
        config:{type:Object, default:null},
        height:{type:Number, default:300},
        updateOptions:{type:Object, default:()=>{return {notMerge:true}}}
    },
    computed: {
        showExport(){
            return this.config && this.config.showExport ? this.config.showExport : false
        },
        mobileMode(){
            return process.env.VUE_APP_MOBILE_MODE ? process.env.VUE_APP_MOBILE_MODE : false
        },
        theme() {
            let theme = this.unifiedIsThemeDark&&!this.whiteMode ? this.darkTheme : this.lightTheme
            theme.backgroundColor = "rgba(0,0,0,0)"
            return theme
        },
        option(){
            return this.config&&this.config!=="" ? this.config :  null
        }
    },
    methods:{
        handleClick(data){
            this.$emit("click",data)
        },
        getPercentages(array) {
            let sum = array.reduce((sum, x) => sum + x);
            return array.map(x => ((x / sum) * 100).toFixed(2))
        },
        downloadCSV() {
            if (!this.config || !this.config.series) return;
            let df = {};
            let series = Array.isArray(this.config.series) ? this.config.series : [this.config.series];
            let chartType = series[0] && series[0].type ? series[0].type : 'line';

            if (chartType === 'pie') {
                // Pie chart: series[0].data = [{name, value}, ...]
                let pieData = series[0].data || [];
                df["Labels"] = pieData.map(d => d.name);
                df["Values"] = pieData.map(d => d.value);
                df["Percentages"] = this.getPercentages(pieData.map(d => d.value));
                exportCSVFromDicts(df);
            } else if (chartType === 'heatmap') {
                // Heatmap: xAxis.data as columns header, yAxis.data as row labels
                let xData = this.config.xAxis && this.config.xAxis.data ? this.config.xAxis.data : [];
                let yData = this.config.yAxis && this.config.yAxis.data ? this.config.yAxis.data : [];
                df["timestamp"] = xData;
                // Heatmap data is [[xIdx, yIdx, value], ...]
                let heatData = series[0].data || [];
                for (let yIdx in yData) {
                    let col = [];
                    for (let xIdx in xData) {
                        let point = heatData.find(d => String(d[0]) === String(xIdx) && String(d[1]) === String(yIdx));
                        col.push(point ? point[2] : null);
                    }
                    df[yData[yIdx]] = col;
                }
                exportCSVFromDicts(df);
            } else if (['line', 'bar', 'scatter', 'effectScatter'].includes(chartType)) {
                // Cartesian charts
                let xAxisData = null;
                let xAxis = this.config.xAxis;
                if (Array.isArray(xAxis)) xAxis = xAxis[0];
                if (xAxis && xAxis.data) {
                    xAxisData = xAxis.data;
                }
                let col1 = "timestamp";
                if (xAxis && xAxis.name && xAxis.name !== "") {
                    col1 = xAxis.name;
                }

                // Check if series data uses [x, y] pairs
                let firstSeriesData = series[0].data || [];
                let hasPairs = firstSeriesData.length > 0 && Array.isArray(firstSeriesData[0]);

                if (hasPairs) {
                    // Data is [[x, y], ...] pairs
                    df[col1] = firstSeriesData.map(d => d[0]);
                    for (let s of series) {
                        let sData = s.data || [];
                        df[s.name || ('series_' + series.indexOf(s))] = sData.map(d => d[1]);
                    }
                } else if (xAxisData) {
                    // Shared category xAxis
                    df[col1] = xAxisData;
                    for (let s of series) {
                        df[s.name || ('series_' + series.indexOf(s))] = s.data || [];
                    }
                } else {
                    // Fallback: use index as x
                    let len = firstSeriesData.length;
                    df[col1] = Array.from({length: len}, (_, i) => i);
                    for (let s of series) {
                        df[s.name || ('series_' + series.indexOf(s))] = s.data || [];
                    }
                }
                exportCSVFromDicts(df);
            } else {
                // Generic fallback for other chart types
                let xAxis = this.config.xAxis;
                if (Array.isArray(xAxis)) xAxis = xAxis[0];
                if (xAxis && xAxis.data) {
                    let col1 = (xAxis.name && xAxis.name !== "") ? xAxis.name : "timestamp";
                    df[col1] = xAxis.data;
                    for (let s of series) {
                        if (s.data && Array.isArray(s.data) && s.data.length > 0 && !Array.isArray(s.data[0]) && typeof s.data[0] !== 'object') {
                            df[s.name || ('series_' + series.indexOf(s))] = s.data;
                        }
                    }
                    if (Object.keys(df).length > 1) {
                        exportCSVFromDicts(df);
                    }
                }
            }
        }
    },
    data() {
        return {
            lightTheme: {
                    "color": [
                        "#c1232b",
                        "#27727b",
                        "#fcce10",
                        "#e87c25",
                        "#b5c334",
                        "#fe8463",
                        "#9bca63",
                        "#fad860",
                        "#f3a43b",
                        "#60c0dd",
                        "#d7504b",
                        "#c6e579",
                        "#f4e001",
                        "#f0805a",
                        "#26c0c0"
                    ],
                    "backgroundColor": "rgba(0,0,0,0)",
                    "textStyle": {},
                    "title": {
                        "textStyle": {
                            "color": "#27727b"
                        },
                        "subtextStyle": {
                            "color": "#aaaaaa"
                        }
                    },
                    "line": {
                        "itemStyle": {
                            "borderWidth": 1
                        },
                        "lineStyle": {
                            "width": "3"
                        },
                        "symbolSize": "5",
                        "symbol": "emptyCircle",
                        "smooth": false
                    },
                    "radar": {
                        "itemStyle": {
                            "borderWidth": 1
                        },
                        "lineStyle": {
                            "width": "3"
                        },
                        "symbolSize": "5",
                        "symbol": "emptyCircle",
                        "smooth": false
                    },
                    "bar": {
                        "itemStyle": {
                            "barBorderWidth": 0,
                            "barBorderColor": "#ccc"
                        }
                    },
                    "pie": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "scatter": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "boxplot": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "parallel": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "sankey": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "funnel": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "gauge": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "candlestick": {
                        "itemStyle": {
                            "color": "#c1232b",
                            "color0": "#b5c334",
                            "borderColor": "#c1232b",
                            "borderColor0": "#b5c334",
                            "borderWidth": 1
                        }
                    },
                    "graph": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        },
                        "lineStyle": {
                            "width": 1,
                            "color": "#404040"
                        },
                        "symbolSize": "5",
                        "symbol": "emptyCircle",
                        "smooth": false,
                        "color": [
                            "#c1232b",
                            "#27727b",
                            "#fcce10",
                            "#e87c25",
                            "#b5c334",
                            "#fe8463",
                            "#9bca63",
                            "#fad860",
                            "#f3a43b",
                            "#60c0dd",
                            "#d7504b",
                            "#c6e579",
                            "#f4e001",
                            "#f0805a",
                            "#26c0c0"
                        ],
                        "label": {
                            "color": "#404040"
                        }
                    },
                    "map": {
                        "itemStyle": {
                            "areaColor": "#dddddd",
                            "borderColor": "#eeeeee",
                            "borderWidth": 0.5
                        },
                        "label": {
                            "color": "#c1232b"
                        },
                        "emphasis": {
                            "itemStyle": {
                                "areaColor": "#fe994e",
                                "borderColor": "#444",
                                "borderWidth": 1
                            },
                            "label": {
                                "color": "rgb(100,0,0)"
                            }
                        }
                    },
                    "geo": {
                        "itemStyle": {
                            "areaColor": "#dddddd",
                            "borderColor": "#eeeeee",
                            "borderWidth": 0.5
                        },
                        "label": {
                            "color": "#c1232b"
                        },
                        "emphasis": {
                            "itemStyle": {
                                "areaColor": "#fe994e",
                                "borderColor": "#444",
                                "borderWidth": 1
                            },
                            "label": {
                                "color": "rgb(100,0,0)"
                            }
                        }
                    },
                    "categoryAxis": {
                        "axisLine": {
                            "show": true,
                            "lineStyle": {
                                "color": "#27727b"
                            }
                        },
                        "axisTick": {
                            "show": true,
                            "lineStyle": {
                                "color": "#27727b"
                            }
                        },
                        "axisLabel": {
                            "show": true,
                            "color": "#333"
                        },
                        "splitLine": {
                            "show": false,
                            "lineStyle": {
                                "color": [
                                    "#ccc"
                                ]
                            }
                        },
                        "splitArea": {
                            "show": false,
                            "areaStyle": {
                                "color": [
                                    "rgba(250,250,250,0.3)",
                                    "rgba(200,200,200,0.3)"
                                ]
                            }
                        }
                    },
                    "valueAxis": {
                        "axisLine": {
                            "show": false,
                            "lineStyle": {
                                "color": "#333"
                            }
                        },
                        "axisTick": {
                            "show": false,
                            "lineStyle": {
                                "color": "#333"
                            }
                        },
                        "axisLabel": {
                            "show": true,
                            "color": "#333"
                        },
                        "splitLine": {
                            "show": true,
                            "lineStyle": {
                                "color": [
                                    "#ccc"
                                ]
                            }
                        },
                        "splitArea": {
                            "show": false,
                            "areaStyle": {
                                "color": [
                                    "rgba(250,250,250,0.3)",
                                    "rgba(200,200,200,0.3)"
                                ]
                            }
                        }
                    },
                    "logAxis": {
                        "axisLine": {
                            "show": true,
                            "lineStyle": {
                                "color": "#27727b"
                            }
                        },
                        "axisTick": {
                            "show": true,
                            "lineStyle": {
                                "color": "#333"
                            }
                        },
                        "axisLabel": {
                            "show": true,
                            "color": "#333"
                        },
                        "splitLine": {
                            "show": true,
                            "lineStyle": {
                                "color": [
                                    "#ccc"
                                ]
                            }
                        },
                        "splitArea": {
                            "show": false,
                            "areaStyle": {
                                "color": [
                                    "rgba(250,250,250,0.3)",
                                    "rgba(200,200,200,0.3)"
                                ]
                            }
                        }
                    },
                    "timeAxis": {
                        "axisLine": {
                            "show": true,
                            "lineStyle": {
                                "color": "#27727b"
                            }
                        },
                        "axisTick": {
                            "show": true,
                            "lineStyle": {
                                "color": "#333"
                            }
                        },
                        "axisLabel": {
                            "show": true,
                            "color": "#333"
                        },
                        "splitLine": {
                            "show": true,
                            "lineStyle": {
                                "color": [
                                    "#ccc"
                                ]
                            }
                        },
                        "splitArea": {
                            "show": false,
                            "areaStyle": {
                                "color": [
                                    "rgba(250,250,250,0.3)",
                                    "rgba(200,200,200,0.3)"
                                ]
                            }
                        }
                    },
                    "toolbox": {
                        "iconStyle": {
                            "borderColor": "#c1232b"
                        },
                        "emphasis": {
                            "iconStyle": {
                                "borderColor": "#e87c25"
                            }
                        }
                    },
                    "legend": {
                        "textStyle": {
                            "color": "#333333"
                        }
                    },
                    "tooltip": {
                        "axisPointer": {
                            "lineStyle": {
                                "color": "#27727b",
                                "width": 1
                            },
                            "crossStyle": {
                                "color": "#27727b",
                                "width": 1
                            }
                        }
                    },
                    "timeline": {
                        "lineStyle": {
                            "color": "#293c55",
                            "width": 1
                        },
                        "itemStyle": {
                            "color": "#27727b",
                            "borderWidth": 1
                        },
                        "controlStyle": {
                            "color": "#27727b",
                            "borderColor": "#27727b",
                            "borderWidth": 0.5
                        },
                        "checkpointStyle": {
                            "color": "#c1232b",
                            "borderColor": "#c23531"
                        },
                        "label": {
                            "color": "#293c55"
                        },
                        "emphasis": {
                            "itemStyle": {
                                "color": "#72d4e0"
                            },
                            "controlStyle": {
                                "color": "#27727b",
                                "borderColor": "#27727b",
                                "borderWidth": 0.5
                            },
                            "label": {
                                "color": "#293c55"
                            }
                        }
                    },
                    "visualMap": {
                        "color": [
                            "#c1232b",
                            "#fcce10"
                        ]
                    },
                    "dataZoom": {
                        "backgroundColor": "rgba(0,0,0,0)",
                        "dataBackgroundColor": "rgba(181,195,52,0.3)",
                        "fillerColor": "rgba(181,195,52,0.2)",
                        "handleColor": "#27727b",
                        "handleSize": "100%",
                        "textStyle": {
                            "color": "#999999"
                        }
                    },
                    "markPoint": {
                        "label": {
                            "color": "#eeeeee"
                        },
                        "emphasis": {
                            "label": {
                                "color": "#eeeeee"
                            }
                        }
                    }
                },
            darkTheme:{
                    "color": [
                        "#dd6b66",
                        "#759aa0",
                        "#e69d87",
                        "#8dc1a9",
                        "#ea7e53",
                        "#eedd78",
                        "#73a373",
                        "#73b9bc",
                        "#7289ab",
                        "#91ca8c",
                        "#f49f42"
                    ],
                    "backgroundColor": "rgb(66,66,66)",
                    "textStyle": {},
                    "title": {
                        "textStyle": {
                            "color": "#eeeeee"
                        },
                        "subtextStyle": {
                            "color": "#aaaaaa"
                        }
                    },
                    "line": {
                        "itemStyle": {
                            "borderWidth": 1
                        },
                        "lineStyle": {
                            "width": 2
                        },
                        "symbolSize": 4,
                        "symbol": "circle",
                        "smooth": false
                    },
                    "radar": {
                        "itemStyle": {
                            "borderWidth": 1
                        },
                        "lineStyle": {
                            "width": 2
                        },
                        "symbolSize": 4,
                        "symbol": "circle",
                        "smooth": false
                    },
                    "bar": {
                        "itemStyle": {
                            "barBorderWidth": 0,
                            "barBorderColor": "#ccc"
                        }
                    },
                    "pie": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "scatter": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "boxplot": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "parallel": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "sankey": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "funnel": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "gauge": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        }
                    },
                    "candlestick": {
                        "itemStyle": {
                            "color": "#fd1050",
                            "color0": "#0cf49b",
                            "borderColor": "#fd1050",
                            "borderColor0": "#0cf49b",
                            "borderWidth": 1
                        }
                    },
                    "graph": {
                        "itemStyle": {
                            "borderWidth": 0,
                            "borderColor": "#ccc"
                        },
                        "lineStyle": {
                            "width": 1,
                            "color": "#aaaaaa"
                        },
                        "symbolSize": 4,
                        "symbol": "circle",
                        "smooth": false,
                        "color": [
                            "#dd6b66",
                            "#759aa0",
                            "#e69d87",
                            "#8dc1a9",
                            "#ea7e53",
                            "#eedd78",
                            "#73a373",
                            "#73b9bc",
                            "#7289ab",
                            "#91ca8c",
                            "#f49f42"
                        ],
                        "label": {
                            "color": "#eeeeee"
                        }
                    },
                    "map": {
                        "itemStyle": {
                            "areaColor": "#eee",
                            "borderColor": "#444",
                            "borderWidth": 0.5
                        },
                        "label": {
                            "color": "#000"
                        },
                        "emphasis": {
                            "itemStyle": {
                                "areaColor": "rgba(255,215,0,0.8)",
                                "borderColor": "#444",
                                "borderWidth": 1
                            },
                            "label": {
                                "color": "rgb(100,0,0)"
                            }
                        }
                    },
                    "geo": {
                        "itemStyle": {
                            "areaColor": "#eee",
                            "borderColor": "#444",
                            "borderWidth": 0.5
                        },
                        "label": {
                            "color": "#000"
                        },
                        "emphasis": {
                            "itemStyle": {
                                "areaColor": "rgba(255,215,0,0.8)",
                                "borderColor": "#444",
                                "borderWidth": 1
                            },
                            "label": {
                                "color": "rgb(100,0,0)"
                            }
                        }
                    },
                    "categoryAxis": {
                        "axisLine": {
                            "show": true,
                            "lineStyle": {
                                "color": "#eeeeee"
                            }
                        },
                        "axisTick": {
                            "show": true,
                            "lineStyle": {
                                "color": "#eeeeee"
                            }
                        },
                        "axisLabel": {
                            "show": true,
                            "color": "#eeeeee"
                        },
                        "splitLine": {
                            "show": true,
                            "lineStyle": {
                                "color": [
                                    "#aaaaaa"
                                ]
                            }
                        },
                        "splitArea": {
                            "show": false,
                            "areaStyle": {
                                "color": [
                                    "#eeeeee"
                                ]
                            }
                        }
                    },
                    "valueAxis": {
                        "axisLine": {
                            "show": true,
                            "lineStyle": {
                                "color": "#eeeeee"
                            }
                        },
                        "axisTick": {
                            "show": true,
                            "lineStyle": {
                                "color": "#eeeeee"
                            }
                        },
                        "axisLabel": {
                            "show": true,
                            "color": "#eeeeee"
                        },
                        "splitLine": {
                            "show": true,
                            "lineStyle": {
                                "color": [
                                    "#aaaaaa"
                                ]
                            }
                        },
                        "splitArea": {
                            "show": false,
                            "areaStyle": {
                                "color": [
                                    "#eeeeee"
                                ]
                            }
                        }
                    },
                    "logAxis": {
                        "axisLine": {
                            "show": true,
                            "lineStyle": {
                                "color": "#eeeeee"
                            }
                        },
                        "axisTick": {
                            "show": true,
                            "lineStyle": {
                                "color": "#eeeeee"
                            }
                        },
                        "axisLabel": {
                            "show": true,
                            "color": "#eeeeee"
                        },
                        "splitLine": {
                            "show": true,
                            "lineStyle": {
                                "color": [
                                    "#aaaaaa"
                                ]
                            }
                        },
                        "splitArea": {
                            "show": false,
                            "areaStyle": {
                                "color": [
                                    "#eeeeee"
                                ]
                            }
                        }
                    },
                    "timeAxis": {
                        "axisLine": {
                            "show": true,
                            "lineStyle": {
                                "color": "#eeeeee"
                            }
                        },
                        "axisTick": {
                            "show": true,
                            "lineStyle": {
                                "color": "#eeeeee"
                            }
                        },
                        "axisLabel": {
                            "show": true,
                            "color": "#eeeeee"
                        },
                        "splitLine": {
                            "show": true,
                            "lineStyle": {
                                "color": [
                                    "#aaaaaa"
                                ]
                            }
                        },
                        "splitArea": {
                            "show": false,
                            "areaStyle": {
                                "color": [
                                    "#eeeeee"
                                ]
                            }
                        }
                    },
                    "toolbox": {
                        "iconStyle": {
                            "borderColor": "#999999"
                        },
                        "emphasis": {
                            "iconStyle": {
                                "borderColor": "#666666"
                            }
                        }
                    },
                    "legend": {
                        "textStyle": {
                            "color": "#eeeeee"
                        }
                    },
                    "tooltip": {
                        "axisPointer": {
                            "lineStyle": {
                                "color": "#eeeeee",
                                "width": "1"
                            },
                            "crossStyle": {
                                "color": "#eeeeee",
                                "width": "1"
                            }
                        }
                    },
                    "timeline": {
                        "lineStyle": {
                            "color": "#eeeeee",
                            "width": 1
                        },
                        "itemStyle": {
                            "color": "#dd6b66",
                            "borderWidth": 1
                        },
                        "controlStyle": {
                            "color": "#eeeeee",
                            "borderColor": "#eeeeee",
                            "borderWidth": 0.5
                        },
                        "checkpointStyle": {
                            "color": "#e43c59",
                            "borderColor": "#c23531"
                        },
                        "label": {
                            "color": "#eeeeee"
                        },
                        "emphasis": {
                            "itemStyle": {
                                "color": "#a9334c"
                            },
                            "controlStyle": {
                                "color": "#eeeeee",
                                "borderColor": "#eeeeee",
                                "borderWidth": 0.5
                            },
                            "label": {
                                "color": "#eeeeee"
                            }
                        }
                    },
                    "visualMap": {
                        "color": [
                            "#bf444c",
                            "#d88273",
                            "#f6efa6"
                        ]
                    },
                    "dataZoom": {
                        "backgroundColor": "rgba(47,69,84,0)",
                        "dataBackgroundColor": "rgba(255,255,255,0.3)",
                        "fillerColor": "rgba(167,183,204,0.4)",
                        "handleColor": "#a7b7cc",
                        "handleSize": "100%",
                        "textStyle": {
                            "color": "#eeeeee"
                        }
                    },
                    "markPoint": {
                        "label": {
                            "color": "#eeeeee"
                        },
                        "emphasis": {
                            "label": {
                                "color": "#eeeeee"
                            }
                        }
                    }
                },

        };
    }};
</script>